import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { PiiField } from '@/components/ui/PiiField'
import { usePresentation } from '@/components/layout/AppShell'

interface ToolStat {
  tool: string
  attempts: number
  uniqueUsers: number
  avgScore: number
  passed: number
  passRate: number
  totalMinutes: number
}

interface OverviewResponse {
  days: number
  dateFrom: string
  tools: ToolStat[]
}

interface TrendsResponse {
  days: number
  dateFrom: string
  trends: { date: string; tool: string; attempts: number; unique_users: number; avg_score: number }[]
}

interface ExerciseResult {
  id: string
  user_id: string
  exercise_id: string
  tool: string
  comparison_score: number | null
  passed: number
  duration_ms: number
  date: string
  created_at: string
}

interface ResultsResponse {
  results: ExerciseResult[]
  total: number
  page: number
  totalPages: number
}

const toolLabels: Record<string, string> = {
  intervals: 'Intervals',
  foundational: 'Foundational',
  'sight-reading': 'Sight Reading',
  vfe: 'VFE',
  vrp: 'VRP',
}

function formatDuration(ms: number): string {
  if (!ms) return '--'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`
}

export function ExerciseOverviewPage() {
  const [days, setDays] = useState(30)
  const { presenting } = usePresentation()

  const { data: overview, isLoading } = useQuery({
    queryKey: ['admin-exercises-overview', days],
    queryFn: () => api.get<OverviewResponse>('/api/admin/exercises/overview', { params: { days } }),
  })

  const { data: trends } = useQuery({
    queryKey: ['admin-exercises-trends', days],
    queryFn: () => api.get<TrendsResponse>('/api/admin/exercises/trends', { params: { days } }),
  })

  const totalAttempts = overview?.tools.reduce((s, t) => s + t.attempts, 0) ?? 0
  const totalUsers = overview?.tools.reduce((s, t) => s + t.uniqueUsers, 0) ?? 0
  const totalMinutes = overview?.tools.reduce((s, t) => s + t.totalMinutes, 0) ?? 0

  // Aggregate daily trends
  const dailyTotals = new Map<string, number>()
  for (const t of trends?.trends ?? []) {
    dailyTotals.set(t.date, (dailyTotals.get(t.date) ?? 0) + t.attempts)
  }
  const sortedDays = [...dailyTotals.entries()].sort((a, b) => a[0].localeCompare(b[0]))

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Exercises</h1>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors cursor-pointer ${
                days === d
                  ? 'bg-[#65A0A8] text-white'
                  : 'bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)]'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <KpiCard label="Total Exercises" value={totalAttempts.toLocaleString()} />
            <KpiCard label="Unique Users" value={totalUsers.toLocaleString()} />
            <KpiCard label="Total Minutes" value={totalMinutes.toLocaleString()} />
          </div>

          {/* Per-tool cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {(overview?.tools ?? []).map((t) => (
              <Card key={t.tool}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-semibold">{toolLabels[t.tool] ?? t.tool}</h3>
                    <Badge variant="brand">{t.attempts}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label="Unique Users" value={t.uniqueUsers.toString()} />
                  <Row label="Avg Score" value={t.avgScore > 0 ? `${t.avgScore.toFixed(1)}%` : '--'} />
                  <Row label="Pass Rate" value={`${t.passRate.toFixed(0)}%`} />
                  <Row label="Total Time" value={`${t.totalMinutes}m`} />
                  <div className="pt-1">
                    <div className="h-2 bg-[var(--surface-nested)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#65A0A8] rounded-full transition-all"
                        style={{ width: `${Math.min(100, t.passRate)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Daily trend bar chart */}
          {sortedDays.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <h3 className="font-heading font-semibold text-sm">Daily Completions</h3>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-[2px] h-32">
                  {sortedDays.map(([date, attempts]) => {
                    const max = Math.max(...sortedDays.map(([, a]) => a))
                    const pct = max > 0 ? (attempts / max) * 100 : 0
                    return (
                      <div
                        key={date}
                        className="flex-1 bg-[#65A0A8]/60 hover:bg-[#65A0A8] rounded-t transition-colors"
                        style={{ height: `${Math.max(2, pct)}%` }}
                        title={`${date}: ${attempts} exercises`}
                      />
                    )
                  })}
                </div>
                <div className="flex justify-between mt-1 text-[10px] text-[var(--content-tertiary)]">
                  <span>{sortedDays[0]?.[0]}</span>
                  <span>{sortedDays[sortedDays.length - 1]?.[0]}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cross-user results browser */}
          <ResultsBrowser presenting={presenting} />
        </>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--content-tertiary)]">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

function ResultsBrowser({ presenting }: { presenting: boolean }) {
  const [page, setPage] = useState(1)
  const [tool, setTool] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-exercises-results', page, tool],
    queryFn: () =>
      api.get<ResultsResponse>('/api/admin/exercises/results', {
        params: { page, limit: 20, tool: tool || undefined },
      }),
  })

  const columns: Column<ExerciseResult>[] = [
    { id: 'date', header: 'Date', cell: (r) => <span className="tabular-nums">{r.date}</span> },
    {
      id: 'user_id', header: 'User',
      cell: (r) => <PiiField value={r.user_id.slice(0, 8) + '...'} presenting={presenting} className="text-xs" />,
    },
    { id: 'tool', header: 'Tool', cell: (r) => <span>{toolLabels[r.tool] ?? r.tool}</span> },
    { id: 'exercise_id', header: 'Exercise', cell: (r) => <span className="text-[var(--content-secondary)] text-xs">{r.exercise_id}</span> },
    {
      id: 'comparison_score', header: 'Score',
      cell: (r) => r.comparison_score != null
        ? <span className="tabular-nums font-medium">{r.comparison_score.toFixed(0)}%</span>
        : <span className="text-[var(--content-tertiary)]">--</span>,
    },
    { id: 'passed', header: 'Passed', cell: (r) => <Badge variant={r.passed ? 'success' : 'error'}>{r.passed ? 'Yes' : 'No'}</Badge> },
    { id: 'duration_ms', header: 'Duration', cell: (r) => <span className="tabular-nums text-[var(--content-secondary)]">{formatDuration(r.duration_ms)}</span> },
  ]

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-sm">All Results</h3>
          <div className="flex items-center gap-3">
            <select
              value={tool}
              onChange={(e) => { setTool(e.target.value); setPage(1) }}
              className="px-3 py-1.5 text-sm rounded-lg bg-[var(--surface-elevated)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
            >
              <option value="">All Tools</option>
              {Object.entries(toolLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {data && <span className="text-sm text-[var(--content-tertiary)]">{data.total} total</span>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          keyExtractor={(r) => r.id}
          page={page}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          loading={isLoading}
          emptyMessage="No results"
        />
      </CardContent>
    </Card>
  )
}
